import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";
import ForumIcon from "@material-ui/icons/Forum";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: '#ffffff',
	},
	internalListIcon: {
		minWidth: 34,
		color: "#0035a2",
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [internalNotifications, setInternalNotifications] = useState([]);
	const [userQueues, setUserQueues] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);
	const internalNotificationsStorageKey = `internalNotifications:${user?.id || "anonymous"}`;

	useEffect(() => {
		const somAtivo = localStorage.getItem("somNotificacoesAtivo") === "true";
		if (somAtivo) {
			soundAlertRef.current = play;
		}

		if (!("Notification" in window)) {
			return console.log("This browser doesn't support notifications");
		}

		Notification.requestPermission();
	}, [play]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		try {
			const stored = localStorage.getItem(internalNotificationsStorageKey);
			if (!stored) {
				setInternalNotifications([]);
				return;
			}
			const parsed = JSON.parse(stored);
			if (Array.isArray(parsed)) {
				setInternalNotifications(parsed);
			}
		} catch {
			setInternalNotifications([]);
		}
	}, [internalNotificationsStorageKey]);

	useEffect(() => {
		localStorage.setItem(
			internalNotificationsStorageKey,
			JSON.stringify(internalNotifications)
		);
	}, [internalNotifications, internalNotificationsStorageKey]);

	useEffect(() => {
		let isMounted = true;

		const loadUserQueues = async () => {
			if (!user?.id) return;
			try {
				const { data } = await api.get(`/users/${user.id}`);
				if (isMounted) {
					setUserQueues(data?.queues || []);
				}
			} catch {
				if (isMounted) {
					setUserQueues([]);
				}
			}
		};

		loadUserQueues();
		return () => {
			isMounted = false;
		};
	}, [user?.id]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => {
			socket.emit("joinNotification");
			userQueues.forEach(queue => {
				socket.emit("joinChatBox", `queue-${queue.id}-notification`);
			});
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}

			if (data.action === "update" && data.ticket?.unreadMessages > 0) {
				const belongsToUser =
					data.ticket.userId === user?.id || !data.ticket.userId;
				const belongsToUserQueues =
					!data.ticket.queueId ||
					userQueues.some(queue => Number(queue.id) === Number(data.ticket.queueId));

				if (!belongsToUser || !belongsToUserQueues) return;

				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && data.message?.queueId && !data.ticket) {
				const queueId = Number(data.message.queueId);
				const queue = userQueues.find(q => Number(q.id) === queueId);
				const notificationId = `internal-${data.message.id}`;
				const senderName = data.message.user?.name || "Colega";
				const internalNotification = {
					id: notificationId,
					queueId,
					queueName: queue?.name || `Fila ${queueId}`,
					senderName,
					body: data.message.body || "Nova mensagem interna",
					createdAt: data.message.createdAt || new Date().toISOString(),
				};

				setInternalNotifications(prevState => {
					const alreadyExists = prevState.some(n => n.id === notificationId);
					if (alreadyExists) return prevState;
					return [internalNotification, ...prevState].slice(0, 30);
				});

				const shouldNotNotificateInternal =
					document.visibilityState === "visible" ||
					(historyRef.current.location.pathname === "/internal-chat" &&
						document.visibilityState === "visible");

				if (!shouldNotNotificateInternal && "Notification" in window && Notification.permission === "granted") {
					const desktopNotification = new Notification(
						`Mensagem interna - ${internalNotification.senderName}`,
						{
							body: `${internalNotification.queueName}: ${internalNotification.body} - ${format(new Date(), "HH:mm")}`,
							tag: notificationId,
							renotify: true,
						}
					);

					desktopNotification.onclick = e => {
						e.preventDefault();
						window.focus();
						historyRef.current.push("/internal-chat");
					};

					setDesktopNotifications(prevState => {
						const notfiticationIndex = prevState.findIndex(
							n => n.tag === desktopNotification.tag
						);
						if (notfiticationIndex !== -1) {
							prevState[notfiticationIndex] = desktopNotification;
							return [...prevState];
						}
						return [desktopNotification, ...prevState];
					});

					soundAlertRef.current && soundAlertRef.current();
				}
				return;
			}

			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					document.visibilityState === "visible" ||
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		return () => {
			userQueues.forEach(queue => {
				socket.emit("leaveChatBox", `queue-${queue.id}-notification`);
			});
			socket.disconnect();
		};
	}, [user, userQueues]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current && soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	const handleOpenInternalChat = () => {
		setIsOpen(false);
		history.push("/internal-chat");
	};

	useEffect(() => {
		const clearIfInternalChatIsOpen = (pathname) => {
			if (pathname === "/internal-chat") {
				setInternalNotifications([]);
				localStorage.removeItem(internalNotificationsStorageKey);
			}
		};

		clearIfInternalChatIsOpen(history.location.pathname);
		const unlisten = history.listen((location) => {
			clearIfInternalChatIsOpen(location.pathname);
		});

		return () => {
			unlisten();
		};
	}, [history, internalNotificationsStorageKey]);

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length + internalNotifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{internalNotifications.map(notification => (
						<ListItem button key={notification.id} onClick={handleOpenInternalChat}>
							<ListItemIcon className={classes.internalListIcon}>
								<ForumIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText
								primary={`Interno - ${notification.senderName}`}
								secondary={notification.body}
							/>
						</ListItem>
					))}
					{notifications.length === 0 && internalNotifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
